import { resolveThreadRootId } from "./catalog.js";
import { normalizeObjectUrl } from "./quotes.js";

// Backlinks include body references as well as the API's single reply parent.
// Keep them separate from repliesByParent: only actual replies belong in the
// reply tree (and in the subtree removed when a post is deleted).
// The caller extracts URLs from HTML so this graph builder stays DOM-free.
export function buildThreadGraph(ctx, referenceUrls = () => []) {
  const statusById = new Map();
  if (ctx?.op) statusById.set(ctx.op.id, ctx.op);
  for (const s of (ctx?.descendants || [])) statusById.set(s.id, s);

  const idByUrl = new Map();
  for (const s of statusById.values()) {
    for (const url of [s.uri, s.url]) {
      if (url) idByUrl.set(normalizeObjectUrl(url), s.id);
    }
  }

  const parentById = new Map();
  const repliesByParent = new Map();
  const backlinksByTarget = new Map();
  for (const s of statusById.values()) {
    const targets = new Set();
    const parentId = s.in_reply_to_id;
    if (parentId && statusById.has(parentId)) {
      parentById.set(s.id, parentId);
      if (!repliesByParent.has(parentId)) repliesByParent.set(parentId, []);
      repliesByParent.get(parentId).push(s.id);
      targets.add(parentId);
    }
    for (const url of referenceUrls(s)) {
      const targetId = idByUrl.get(normalizeObjectUrl(url));
      if (targetId && targetId !== s.id) targets.add(targetId);
    }
    for (const targetId of targets) {
      if (!backlinksByTarget.has(targetId)) backlinksByTarget.set(targetId, []);
      backlinksByTarget.get(targetId).push(s.id);
    }
  }

  for (const map of [repliesByParent, backlinksByTarget]) {
    for (const ids of map.values()) {
      ids.sort((a, b) => (statusById.get(a)?.created_at || "")
        .localeCompare(statusById.get(b)?.created_at || ""));
    }
  }
  return { statusById, parentById, repliesByParent, backlinksByTarget };
}

// Resolve a thread root id from a parent map (childId -> parentId).
export function resolveRootIdFromParentMap(startId, parentById) {
  if (!startId || !parentById) return null;

  let curId = startId;
  const seen = new Set();
  while (curId && !seen.has(curId)) {
    seen.add(curId);
    const parentId = parentById.get(curId);
    if (!parentId) return curId;
    curId = parentId;
  }

  return curId || null;
}

// Resolve the OP/root id from a status context payload:
// { op, ancestors[] } as returned by /api/v1/statuses/:id/context plus status.
export function resolveRootIdFromContext(ctx) {
  if (!ctx?.op?.id) return null;

  const byId = new Map();
  for (const s of (ctx.ancestors || [])) {
    if (s?.id) byId.set(s.id, s);
  }
  byId.set(ctx.op.id, ctx.op);

  return resolveThreadRootId(ctx.op, byId) || null;
}

// Every post hanging below `startId`, at any depth, in a parent -> childIds
// map. Mitra deletes a post's whole subtree along with it, so this is what a
// delete takes with it — of the posts that are loaded, at least. `startId`
// itself is not included.
export function collectDescendantIds(startId, repliesByParent) {
  const found = new Set();
  if (!startId || !repliesByParent) return found;

  const stack = [startId];
  const seen = new Set(stack);
  while (stack.length) {
    for (const childId of repliesByParent.get(stack.pop()) || []) {
      // A cycle can't happen in a well-formed thread, but a malformed parent
      // map shouldn't hang the tab.
      if (seen.has(childId)) continue;
      seen.add(childId);
      found.add(childId);
      stack.push(childId);
    }
  }
  return found;
}

// How many posts a delete would take with it.
export function countDescendants(startId, repliesByParent) {
  return collectDescendantIds(startId, repliesByParent).size;
}

// Build a reply tree from a flat list of descendant statuses. Each node is
// { status, children[] }. A status hangs off its in_reply_to_id parent when
// that parent is present in the set (and isn't the OP); otherwise it becomes a
// root (a direct reply to the OP, or an orphan whose parent wasn't loaded).
// Roots and siblings are sorted oldest → newest by created_at.
export function buildReplyTree(descendants, opId) {
  const byId = new Map();
  for (const s of descendants) byId.set(s.id, { status: s, children: [] });
  const roots = [];
  for (const s of descendants) {
    const node = byId.get(s.id);
    const parentId = s.in_reply_to_id;
    if (parentId && byId.has(parentId) && parentId !== opId) {
      byId.get(parentId).children.push(node);
    } else {
      roots.push(node);
    }
  }
  const byTime = (a, b) =>
    (a.status.created_at || "").localeCompare(b.status.created_at || "");
  const sortRec = (n) => {
    n.children.sort(byTime);
    n.children.forEach(sortRec);
  };
  roots.sort(byTime);
  roots.forEach(sortRec);
  return roots;
}

// Flatten a reply tree into a single chronological list while guaranteeing
// that every reply appears after the post it replies to.
//
// created_at comes from each post's originating server, so a reply can carry
// an earlier timestamp than its parent (clock skew across federated servers).
// A plain time sort would then place the reply *before* the post it answers,
// which reads backwards. Instead we walk the tree in timestamp order but only
// release a node once its parent has already been emitted — a priority-ordered
// topological sort. When clocks agree this is identical to a time sort; when a
// reply is skewed early it simply lands right after its parent (and this holds
// transitively for chains of skewed replies).
export function flattenThreadChronological(tree) {
  const byTime = (a, b) =>
    (a.status.created_at || "").localeCompare(b.status.created_at || "");
  // "ready" = nodes whose parent has already been emitted (roots to start).
  const ready = [...tree].sort(byTime);
  const out = [];
  while (ready.length) {
    const node = ready.shift();   // earliest post whose parent is already placed
    out.push(node.status);
    if (node.children?.length) {
      // Merge this node's children into the ready set, keeping it time-ordered.
      ready.push(...node.children);
      ready.sort(byTime);
    }
  }
  return out;
}
